"use client"

/**
 * DS `FilterRow` — the library's ONE category control on a fine-pointer /
 * desktop viewport.
 *
 * The design system's FilterRow.prompt.md is explicit about the overflow
 * strategy for 36 long-labelled categories:
 *
 *   "Overflow is wrap-to-two-lines plus a '{N} more' disclosure. Not a
 *    horizontal scroller: that hides the long tail behind a gesture with no
 *    affordance. Not a sheet on desktop: that costs a click for the common
 *    case."
 *   "Zero-count facets stay visible and disabled. Hiding them makes the row
 *    reflow under a finger that is already moving."
 *   "On mobile the same component becomes a Sheet trigger with an
 *    applied-count badge."
 *
 * So this file no longer branches on pointer type: the horizontal coarse-
 * pointer scroller it used to render is the exact affordance the spec
 * refuses. The mobile Sheet-trigger half of the contract lives one level up
 * in `components/ui/search-filters.tsx`, which shows this row at `lg+` and the
 * trigger below it — one control per breakpoint, never both.
 *
 * The pills stay real `<a href="/sounds/category/…">` links so the category
 * pages remain crawlable from the library; a plain left-click is intercepted
 * into an in-place filter change, modifier clicks keep native behavior.
 */

import React, { useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react'
import Link from 'next/link'
import { type Category } from '@/lib/supabase'
import { categoryBrowseHref } from '@/lib/category-data'

export const activeFilterClassName =
  'border-[color:color-mix(in_srgb,var(--tls-brand)_28%,var(--tls-border-subtle))] bg-[color:color-mix(in_srgb,var(--tls-brand)_10%,var(--tls-surface))] text-[var(--tls-fg-strong)]'
export const neutralFilterClassName =
  'border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface)] text-[var(--tls-fg-muted)] hover:bg-[var(--tls-surface-hover)] hover:text-[var(--tls-fg-strong)]'

// Base pill: shrinkable + wrappable (min-w-0 overrides the flex-item default
// min-width:auto that would otherwise stop the label from shrinking below its
// single-line intrinsic width; break-words lets an unbroken label wrap rather
// than force the pill wider than its row). A fixed-width pill here overflowed
// the viewport at 200% root font-size (WCAG 1.4.4/1.4.10). `tls-target` sizes
// the hit area BY POINTER (44 fine / 48 coarse), not by breakpoint.
const PILL_CLASS =
  'pill-interactive tls-target flex min-w-0 max-w-full items-center gap-1.5 break-words [overflow-wrap:anywhere] rounded-[var(--radius-pill)] border px-3 py-1.5 text-sm'

// ─── Pointer detection ──────────────────────────────────────────────────────
// Retained for consumers that need to branch behavior (not layout) on pointer
// type. This row itself no longer uses it — see the header comment.

function hasMatchMedia(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
}
function subscribeCoarsePointer(callback: () => void) {
  if (!hasMatchMedia()) return () => {}
  const mq = window.matchMedia('(pointer: coarse)')
  mq.addEventListener('change', callback)
  return () => mq.removeEventListener('change', callback)
}
function getCoarsePointerSnapshot() {
  // jsdom (unit tests) doesn't implement matchMedia — treat as fine pointer,
  // same as the SSR default, rather than throwing.
  if (!hasMatchMedia()) return false
  return window.matchMedia('(pointer: coarse)').matches
}
function getCoarsePointerServerSnapshot() {
  return false
}

/** True on a coarse (touch) primary pointer. False (fine) on server and until hydration settles. */
export function useCoarsePointer(): boolean {
  return useSyncExternalStore(subscribeCoarsePointer, getCoarsePointerSnapshot, getCoarsePointerServerSnapshot)
}

// ─── Shared pill ────────────────────────────────────────────────────────────

interface PillItem {
  key: string
  label: string
  count?: number
  href?: string
  selected: boolean
  /** Zero-count facet: rendered, greyed, not actionable (never hidden). */
  disabled?: boolean
  onSelect: () => void
}

function PillCount({ count }: { count: number }) {
  return (
    <span className="ml-0.5 text-[0.6875rem] tabular-nums text-[var(--tls-fg-subtle)]">
      {count.toLocaleString('en-US')}
    </span>
  )
}

function Pill({
  item,
  pillRef,
}: {
  item: PillItem
  pillRef?: (el: HTMLElement | null) => void
}) {
  const className = `${PILL_CLASS} ${item.selected ? activeFilterClassName : neutralFilterClassName}${
    item.disabled ? ' pointer-events-none opacity-40' : ''
  }`

  // A zero-count facet stays in the row but is inert — a disabled button, not
  // a link, so nothing navigates to an empty category page.
  if (item.disabled) {
    return (
      <button
        type="button"
        ref={pillRef as React.Ref<HTMLButtonElement>}
        disabled
        aria-disabled="true"
        className={className}
      >
        <span className="font-medium">{item.label}</span>
        {item.count !== undefined && <PillCount count={item.count} />}
      </button>
    )
  }

  if (item.href) {
    return (
      <Link prefetch={false}
        ref={pillRef as React.Ref<HTMLAnchorElement>}
        href={item.href}
        aria-current={item.selected ? 'true' : undefined}
        onClick={(e) => {
          // Modifier/middle clicks must keep native link behavior (open in
          // new tab/window); only a plain left-click becomes an in-place
          // filter selection.
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
          e.preventDefault()
          item.onSelect()
        }}
        className={className}
      >
        <span className="font-medium">{item.label}</span>
        {item.count !== undefined && <PillCount count={item.count} />}
      </Link>
    )
  }

  return (
    <button
      type="button"
      ref={pillRef as React.Ref<HTMLButtonElement>}
      onClick={item.onSelect}
      aria-pressed={item.selected}
      className={className}
    >
      <span className="font-medium">{item.label}</span>
      {item.count !== undefined && <PillCount count={item.count} />}
    </button>
  )
}

function buildPills(
  categories: Category[],
  categoryCounts: Record<string, number>,
  allCategoryCount: number,
  activeCategory: string,
  onSelectCategory: (name: string) => void
): PillItem[] {
  return [
    {
      key: 'all',
      label: 'All',
      count: allCategoryCount > 0 ? allCategoryCount : undefined,
      selected: activeCategory === 'All',
      onSelect: () => onSelectCategory('All'),
    },
    ...categories.map((category) => {
      const count = categoryCounts[category.name]
      // Only a *known* zero disables. An absent count means "not loaded yet",
      // which must stay actionable.
      const isEmpty = count === 0
      return {
        key: category.id,
        label: category.name,
        count,
        href: isEmpty ? undefined : categoryBrowseHref(category.name),
        selected: activeCategory === category.name,
        disabled: isEmpty,
        onSelect: () => onSelectCategory(category.name),
      }
    }),
  ]
}

// ─── Wrap to 2 lines, then a "{N} more" disclosure ─────────────────────────

const TWO_LINE_ROW_TOLERANCE = 4

// eslint-disable-next-line react-doctor/no-giant-component -- measurement pass + visible row are one unit; splitting them would duplicate the pill list
function FineRow({
  pills,
  onClearAll,
  hasActiveFilter,
}: {
  pills: PillItem[]
  onClearAll?: () => void
  hasActiveFilter?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  // null = not yet measured (SSR + first paint): render everything so
  // server-rendered category links stay crawlable before hydration.
  const [visibleCount, setVisibleCount] = useState<number | null>(null)
  const measureRefs = useRef<(HTMLElement | null)[]>([])
  const measureWrapRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const measure = () => {
      const items = measureRefs.current.slice(0, pills.length).filter((el): el is HTMLElement => el !== null)
      if (items.length === 0) return
      let rows = 0
      let lastTop = -Infinity
      let cut = items.length
      for (let i = 0; i < items.length; i++) {
        const top = items[i]!.offsetTop
        if (top > lastTop + TWO_LINE_ROW_TOLERANCE) {
          rows++
          lastTop = top
        }
        if (rows > 2) {
          cut = i
          break
        }
      }
      setVisibleCount(cut)
    }
    measure()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
    if (ro && measureWrapRef.current) ro.observe(measureWrapRef.current)
    if (!ro) window.addEventListener('resize', measure)
    return () => {
      ro?.disconnect()
      if (!ro) window.removeEventListener('resize', measure)
    }
  }, [pills])

  const overflowed = visibleCount !== null && visibleCount < pills.length
  const shown = expanded || visibleCount === null ? pills : pills.slice(0, visibleCount)
  const hiddenCount = pills.length - (visibleCount ?? pills.length)

  return (
    <div className="relative min-w-0">
      {/* Off-screen measurement copy — always renders the full, unclamped
          list so the visible-count calc never depends on its own output
          (which would risk a measure/render feedback loop). */}
      <div
        ref={measureWrapRef}
        aria-hidden="true"
        className="pointer-events-none absolute left-0 top-0 flex w-full flex-wrap gap-1.5 opacity-0"
        style={{ visibility: 'hidden' }}
      >
        {pills.map((item, i) => (
          <Pill key={item.key} item={item} pillRef={(el) => { measureRefs.current[i] = el }} />
        ))}
      </div>

      <div
        role="group"
        aria-label="Filter by category"
        className="flex flex-wrap items-center gap-1.5"
      >
        {shown.map((item) => (
          <Pill key={item.key} item={item} />
        ))}
        {/* DS FilterRow: the disclosure is a bare accent text button that
            reads "{N} more" / "Fewer", never a "+N" chip. */}
        {overflowed && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="tlsr-focus tls-target whitespace-nowrap bg-transparent px-1 py-2 text-[13px] font-semibold text-[var(--tls-brand-ink)]"
          >
            {expanded ? 'Fewer' : `${hiddenCount} more`}
          </button>
        )}
        {/* DS FilterRow: "Clear all" is right-aligned (margin-left auto) and
            appears ONLY while something is applied. */}
        {hasActiveFilter && onClearAll && (
          <button
            type="button"
            onClick={onClearAll}
            className="tlsr-focus tls-target ml-auto whitespace-nowrap bg-transparent px-1 py-2 text-[13px] font-semibold text-[var(--tls-fg-muted)] transition-colors hover:text-[var(--tls-fg-strong)]"
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  )
}

// ─── Public component ──────────────────────────────────────────────────────

export interface CategoryOverflowRowProps {
  categories: Category[]
  categoryCounts: Record<string, number>
  allCategoryCount: number
  activeCategory: string
  onSelectCategory: (name: string) => void
  /** Clears every applied filter. Rendered only alongside `hasActiveFilter`. */
  onClearAll?: () => void
  hasActiveFilter?: boolean
}

export function CategoryOverflowRow({
  categories,
  categoryCounts,
  allCategoryCount,
  activeCategory,
  onSelectCategory,
  onClearAll,
  hasActiveFilter,
}: CategoryOverflowRowProps) {
  const pills = buildPills(categories, categoryCounts, allCategoryCount, activeCategory, onSelectCategory)

  if (categories.length === 0) return null

  return <FineRow pills={pills} onClearAll={onClearAll} hasActiveFilter={hasActiveFilter} />
}

export default CategoryOverflowRow
